import { useCallback, useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react';
import { useLayerEscape } from '../../../../hooks/useLayerEscape';
import { browserEscapeOwner } from './browserEscape';
import { DETAILS_PANE_WIDTH_CLASS } from './browserStyles';
import { PANE_MOTION_EASING, PANE_MOTION_MS, prefersReducedMotion } from './pane/paneMotion';

const LAYER_SELECTOR = '[role="dialog"]';

// Runs on mount only, so un-hiding a tab with its pane already open doesn't replay it.
function usePaneEntrance(paneRef: RefObject<HTMLElement>): void {
    useLayoutEffect(() => {
        const pane = paneRef.current;
        if (!pane || typeof pane.animate !== 'function') return;
        if (prefersReducedMotion()) return;
        const animation = pane.animate([{ width: '0px' }, { width: `${pane.offsetWidth}px` }], {
            duration: PANE_MOTION_MS,
            easing: PANE_MOTION_EASING
        });
        return () => animation.cancel();
    }, [paneRef]);
}

// Any dialog inside the browser modal (popover, gallery or 3D overlay) sits above the pane, and so
// does a dialog outside it that holds focus.
function layerAbovePane(modal: Element | null, target: EventTarget | null): boolean {
    if (modal?.querySelector(LAYER_SELECTOR)) return true;
    const focusLayer = target instanceof Element ? target.closest(LAYER_SELECTOR) : null;
    return focusLayer !== null && focusLayer !== modal;
}

// A hidden tab's scope has no client rects, so its pane never takes Escape.
function paneOwnsEscape(scope: HTMLElement | null, event: KeyboardEvent): boolean {
    if (!scope) return false;
    const owner = browserEscapeOwner({
        usedByFocus: event.defaultPrevented,
        layerOpen: layerAbovePane(scope.closest(LAYER_SELECTOR), event.target),
        paneOpen: scope.getClientRects().length > 0
    });
    return owner === 'pane';
}

interface DetailsPaneProps {
    label: string;
    escapeScopeRef: RefObject<HTMLElement>;
    onClose: () => void;
    children: ReactNode;
}

export function DetailsPane({ label, escapeScopeRef, onClose, children }: DetailsPaneProps) {
    const paneRef = useRef<HTMLElement>(null);
    usePaneEntrance(paneRef);
    const ownsEscape = useCallback(
        (event: KeyboardEvent) => paneOwnsEscape(escapeScopeRef.current, event),
        [escapeScopeRef]
    );
    useLayerEscape(onClose, ownsEscape);

    return (
        <section
            ref={paneRef}
            aria-label={label}
            className="flex min-h-0 shrink-0 justify-end overflow-hidden border-l border-gray-200 bg-gray-50"
        >
            <div className={`${DETAILS_PANE_WIDTH_CLASS} h-full min-h-0 shrink-0`}>{children}</div>
        </section>
    );
}
